import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Menu } from '@headlessui/react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  BarChart3,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  Circle,
  FileUp,
  Globe,
  HelpCircle,
  Eye,
  Link2,
  LayoutPanelLeft,
  Lock,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  MoreVertical,
  Plus,
  Printer,
  Puzzle,
  Copy,
  Palette,
  Redo2,
  Search,
  Settings,
  Smartphone,
  Star,
  Tablet,
  Type,
  Undo2,
  UserPlus,
  // @ts-ignore
  Wand2,
  X,
  Code2,
  Keyboard,
  Trash2,
  FilePenLine,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import FieldPalette from '@/components/form-builder/FieldPalette';
import FieldPropertiesPanel from '@/components/form-builder/FieldPropertiesPanel';
import FormCanvas from '@/components/form-builder/FormCanvas';
import FormPreview from '@/components/form-builder/FormPreview';
import FormSettingsPanel from '@/components/form-builder/FormSettingsPanel';
import { Sidebar } from '@/components/layout/Sidebar';
import NotificationDropdown from '@/components/notifications/NotificationDropdown';
import Avatar from '@/components/ui/Avatar';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import ThemeSelector from '@/components/ui/ThemeSelector';
import { useAuthStore } from '@/store/auth';
import { AidaFormsLogo } from '@/components/ui/LuminaLogo';

export interface FormField {
  id: string;
  fieldId: string;
  fieldName?: string;
  fieldLabel: string;
  fieldType: 'text' | 'email' | 'number' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'date' | 'file';
  fieldPlaceholder?: string;
  required?: boolean;
  options?: string[];
  defaultValue?: string;
  step?: number;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  conditional?: {
    field: string;
    operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
    value: any;
  };
  events?: {
    onChange?: string;
    onBlur?: string;
    onFocus?: string;
    onValidate?: string;
  };
}

export interface FormBuilderState {
  formName: string;
  formTitle: string;
  formDescription: string;
  fields: FormField[];
  submitButtonText: string;
  successMessage: string;
  redirectUrl: string;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    customCSS: string;
  };
  config: {
    allowMultipleSubmissions: boolean;
    showProgressBar: boolean;
    enableCaptcha: boolean;
    multiStep: boolean;
    stepsPerPage: number;
    shuffleQuestions: boolean;
    collectEmailMode: 'none' | 'manual';
  };
}

interface FormSubmission {
  id: string;
  data: Record<string, any>;
  submittedAt: string;
}

interface FormBuilderProps {
  initialData?: Partial<FormBuilderState>;
  onSave?: (data: FormBuilderState) => void;
  onCancel?: () => void;
  mode?: 'standalone' | 'editor';
  formId?: string;
}

const FormBuilder: React.FC<FormBuilderProps> = ({
  initialData,
  onSave,
  // @ts-ignore
  onCancel,
  // @ts-ignore
  mode = 'standalone',
  formId,
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation('common');
  const tt = (key: string, fallback: string, options?: Record<string, unknown>) =>
    t(key, { defaultValue: fallback, ...options });
  const currentUserName =
    user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Usuário' : 'Usuário';
  const currentUserEmail = user?.email || 'usuario@aida-forms.local';
  const currentUserInitial = (currentUserName || currentUserEmail).trim().charAt(0).toUpperCase() || 'U';
  const getFieldTypeLabel = (fieldType: FormField['fieldType']) => {
    const labels: Record<FormField['fieldType'], string> = {
      text: tt('formBuilder.palette.types.text', 'Texto curto'),
      email: tt('formBuilder.palette.types.email', 'E-mail'),
      number: tt('formBuilder.palette.types.number', 'Número'),
      textarea: tt('formBuilder.palette.types.textarea', 'Parágrafo'),
      select: tt('formBuilder.palette.types.select', 'Dropdown'),
      checkbox: tt('formBuilder.palette.types.checkbox', 'Caixas de seleção'),
      radio: tt('formBuilder.palette.types.radio', 'Múltipla escolha'),
      date: tt('formBuilder.palette.types.date', 'Data'),
      file: tt('formBuilder.palette.types.file', 'Upload de arquivo'),
    };
    return labels[fieldType];
  };

  const [formState, setFormState] = useState<FormBuilderState>({
    formName: initialData?.formName || '',
    formTitle: initialData?.formTitle || tt('formBuilder.defaults.formTitle', 'Untitled form'),
    formDescription: initialData?.formDescription || '',
    fields: initialData?.fields || [],
    submitButtonText: initialData?.submitButtonText || tt('formBuilder.defaults.submitButton', 'Submit'),
    successMessage: initialData?.successMessage || tt('formBuilder.defaults.successMessage', 'Form submitted successfully!'),
    redirectUrl: initialData?.redirectUrl || '',
    theme: initialData?.theme || {
      primaryColor: '#e20074',
      secondaryColor: '#7f123f',
      fontFamily: 'inherit',
      customCSS: '',
    },
    config: initialData?.config || {
      allowMultipleSubmissions: true,
      showProgressBar: false,
      enableCaptcha: false,
      multiStep: false,
      stepsPerPage: 5,
      shuffleQuestions: false,
      collectEmailMode: 'none',
    },
  });

  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [showSettings, setShowSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPalette, setShowPalette] = useState(true);
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [activeTab, setActiveTab] = useState<'questions' | 'responses' | 'settings'>('questions');
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAppsScriptModal, setShowAppsScriptModal] = useState(false);
  const [shareModalView, setShareModalView] = useState<'main' | 'settings' | 'help'>('main');
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [shortcutsQuery, setShortcutsQuery] = useState('');
  useEffect(() => {
    if (activeTab === 'responses' && formId) {
      setSubmissionsLoading(true);
      api.get('/flow-engine/form-submissions', { params: { formId, limit: 200 } })
        .then(r => setSubmissions(r.data?.data || []))
        .catch((err) => {
          console.error('[Responses] Falha ao carregar respostas:', err?.response?.status ?? err?.code, err?.message);
          setSubmissions([]);
        })
        .finally(() => setSubmissionsLoading(false));
    }
  }, [activeTab, formId]);

  const [settingsSections, setSettingsSections] = useState({
    responses: false,
    presentation: false,
    formDefaults: false,
    questionDefaults: false,
  });
  const [settingsUi, setSettingsUi] = useState({
    collectEmailMode: 'none',
    sendResponseCopy: 'disabled',
    allowResponseEdit: false,
    limitToOneResponse: false,
    shuffleQuestions: false,
    showAnotherResponseLink: true,
    showResultsSummary: false,
    disableAutosave: false,
    defaultCollectEmailMode: 'none',
    defaultQuestionsRequired: false,
    editingConfirmationMessage: false,
    confirmationDraft: initialData?.successMessage || 'Sua resposta foi registrada.',
    editorAccess: 'restricted',
    participantAccess: 'public',
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const addField = useCallback((fieldType: FormField['fieldType']) => {
    const timestamp = Date.now();
    const fieldIdPrefixes: Record<FormField['fieldType'], string> = {
      text: 'texto',
      email: 'email',
      number: 'numero',
      textarea: 'paragrafo',
      select: 'selecao',
      checkbox: 'caixas',
      radio: 'multipla_escolha',
      date: 'data',
      file: 'arquivo',
    };
    const prefix = fieldIdPrefixes[fieldType];
    const nextIndex =
      formState.fields.filter((field) => field.fieldId?.startsWith(`${prefix}_`)).length + 1;
    const newField: FormField = {
      id: `field-${timestamp}`,
      fieldId: `${prefix}_${nextIndex}`,
      fieldName: `${prefix}_${nextIndex}`,
      fieldLabel: tt('formBuilder.defaults.newFieldLabel', 'Pergunta de {{type}}', {
        type: getFieldTypeLabel(fieldType),
      }),
      fieldType,
      required: settingsUi.defaultQuestionsRequired,
    };

    if (fieldType === 'select' || fieldType === 'checkbox' || fieldType === 'radio') {
      newField.options = [
        tt('formBuilder.defaults.optionOne', 'Option 1'),
        tt('formBuilder.defaults.optionTwo', 'Option 2'),
        tt('formBuilder.defaults.optionThree', 'Option 3'),
      ];
    }

    setFormState((prev) => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
    setSelectedFieldId(newField.id);
    setShowSettings(false);
    setActiveTab('questions');
  }, [formState.fields, t, settingsUi.defaultQuestionsRequired]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    if (active.id.toString().startsWith('palette-')) {
      const fieldType = active.id.toString().replace('palette-', '') as FormField['fieldType'];
      addField(fieldType);
    } else if (active.id !== over.id) {
      setFormState((prev) => {
        const oldIndex = prev.fields.findIndex((f) => f.id === active.id);
        const newIndex = prev.fields.findIndex((f) => f.id === over.id);
        return { ...prev, fields: arrayMove(prev.fields, oldIndex, newIndex) };
      });
    }

    setActiveId(null);
  };

  const handleFieldUpdate = useCallback((fieldId: string, updates: Partial<FormField>) => {
    setFormState((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => (field.id === fieldId ? { ...field, ...updates } : field)),
    }));
  }, []);

  const handleFieldDelete = useCallback((fieldId: string) => {
    setFormState((prev) => ({
      ...prev,
      fields: prev.fields.filter((field) => field.id !== fieldId),
    }));
    if (selectedFieldId === fieldId) setSelectedFieldId(null);
  }, [selectedFieldId]);

  const handleFieldDuplicate = useCallback((fieldId: string) => {
    setFormState((prev) => {
      const fieldToDuplicate = prev.fields.find((f) => f.id === fieldId);
      if (!fieldToDuplicate) return prev;

      const timestamp = Date.now();
      const duplicatedField = {
        ...fieldToDuplicate,
        id: `field-${timestamp}`,
        fieldId: `field_${timestamp}`,
        fieldLabel: `${fieldToDuplicate.fieldLabel} (${tt('actions.duplicate', 'Duplicate')})`,
      };

      const index = prev.fields.findIndex((f) => f.id === fieldId);
      const newFields = [...prev.fields];
      newFields.splice(index + 1, 0, duplicatedField);
      return { ...prev, fields: newFields };
    });
  }, [t]);

  const handleFormSettingsUpdate = useCallback((updates: Partial<FormBuilderState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleSave = () => {
    if (onSave) onSave(formState);
  };

  const handleDuplicateForm = () => {
    setFormState((prev) => ({
      ...prev,
      formTitle: `${prev.formTitle || tt('formBuilder.defaults.formTitle', 'Formulário sem título')} (${tt('actions.duplicate', 'Duplicar')})`,
      formName: prev.formName ? `${prev.formName}-copy` : 'formulario-copia',
    }));
    toast.success(tt('formBuilder.actions.copyCreated', 'Cópia criada no editor'));
  };

  const handleAutofillTemplate = () => {
    if (formState.fields.length > 0) {
      toast(tt('formBuilder.actions.autofillSkipped', 'Limpe o formulário se quiser aplicar um modelo automático por cima.'));
      return;
    }
    addField('text');
    setTimeout(() => addField('email'), 0);
    setTimeout(() => addField('radio'), 0);
    toast.success(tt('formBuilder.actions.autofillApplied', 'Estrutura inicial adicionada'));
  };

  const handleEmbedHtml = async () => {
    const slug = formState.formName || 'formulario';
    const embedCode = `<iframe src="${window.location.origin}/form/${slug}" width="100%" height="720" frameborder="0"></iframe>`;
    await navigator.clipboard.writeText(embedCode);
    toast.success(tt('formBuilder.actions.embedCopied', 'Código de incorporação copiado'));
  };

  const handleCopyPublicLink = async () => {
    const slug = formState.formName || 'formulario';
    await navigator.clipboard.writeText(`${window.location.origin}/form/${slug}`);
    toast.success(tt('formBuilder.actions.linkCopied', 'Link copiado'));
  };

  const toJsString = (value: string) => JSON.stringify(value ?? '');

  const buildAppsScript = () => {
    const lines: string[] = [
      '/**',
      ' * Script gerado pela AIDA Forms.',
      ' * Cole este arquivo em um projeto do Google Apps Script e execute `createAidaFormsForm`.',
      ' */',
      'function createAidaFormsForm() {',
      `  const form = FormApp.create(${toJsString(formState.formTitle || tt('formBuilder.defaults.formTitle', 'Formulário sem título'))});`,
      '',
    ];

    if (formState.formDescription?.trim()) {
      lines.push(`  form.setDescription(${toJsString(formState.formDescription)});`);
    }

    if (formState.successMessage?.trim()) {
      lines.push(`  form.setConfirmationMessage(${toJsString(formState.successMessage)});`);
    }

    lines.push(`  form.setCollectEmail(${settingsUi.collectEmailMode !== 'none'});`);
    lines.push(`  form.setAllowResponseEdits(${settingsUi.allowResponseEdit});`);
    lines.push(`  form.setLimitOneResponsePerUser(${settingsUi.limitToOneResponse});`);
    lines.push(`  form.setShowProgressBar(${formState.config.showProgressBar});`);
    lines.push(`  form.setShuffleQuestions(${settingsUi.shuffleQuestions});`);
    lines.push(`  form.setShowLinkToRespondAgain(${settingsUi.showAnotherResponseLink});`);
    lines.push('');
    lines.push('  // Vincula automaticamente uma planilha para armazenar respostas.');
    lines.push(`  const responseSheet = SpreadsheetApp.create(${toJsString(`${formState.formTitle || 'Formulário'} - Respostas`)});`);
    lines.push('  form.setDestination(FormApp.DestinationType.SPREADSHEET, responseSheet.getId());');
    lines.push('');

    formState.fields.forEach((field, index) => {
      const baseVar = `item${index + 1}`;
      const title = field.fieldLabel?.trim() || `Pergunta ${index + 1}`;

      lines.push(`  // Campo ${index + 1}: ${title}`);

      switch (field.fieldType) {
        case 'text':
        case 'email':
        case 'number':
          lines.push(`  const ${baseVar} = form.addTextItem();`);
          lines.push(`  ${baseVar}.setTitle(${toJsString(title)});`);
          if (field.fieldPlaceholder?.trim()) {
            lines.push(`  ${baseVar}.setHelpText(${toJsString(field.fieldPlaceholder)});`);
          }
          if (field.required) {
            lines.push(`  ${baseVar}.setRequired(true);`);
          }
          if (field.fieldType === 'email') {
            lines.push(`  ${baseVar}.setValidation(FormApp.createTextValidation().requireTextIsEmail().build());`);
          }
          if (field.fieldType === 'number') {
            lines.push(`  ${baseVar}.setValidation(FormApp.createTextValidation().requireNumber().build());`);
          }
          break;

        case 'textarea':
          lines.push(`  const ${baseVar} = form.addParagraphTextItem();`);
          lines.push(`  ${baseVar}.setTitle(${toJsString(title)});`);
          if (field.fieldPlaceholder?.trim()) {
            lines.push(`  ${baseVar}.setHelpText(${toJsString(field.fieldPlaceholder)});`);
          }
          if (field.required) {
            lines.push(`  ${baseVar}.setRequired(true);`);
          }
          break;

        case 'select':
          lines.push(`  const ${baseVar} = form.addListItem();`);
          lines.push(`  ${baseVar}.setTitle(${toJsString(title)});`);
          lines.push(`  ${baseVar}.setChoiceValues(${JSON.stringify(field.options || [])});`);
          if (field.required) {
            lines.push(`  ${baseVar}.setRequired(true);`);
          }
          break;

        case 'checkbox':
          lines.push(`  const ${baseVar} = form.addCheckboxItem();`);
          lines.push(`  ${baseVar}.setTitle(${toJsString(title)});`);
          lines.push(`  ${baseVar}.setChoiceValues(${JSON.stringify(field.options || [])});`);
          if (field.required) {
            lines.push(`  ${baseVar}.setRequired(true);`);
          }
          break;

        case 'radio':
          lines.push(`  const ${baseVar} = form.addMultipleChoiceItem();`);
          lines.push(`  ${baseVar}.setTitle(${toJsString(title)});`);
          lines.push(`  ${baseVar}.setChoiceValues(${JSON.stringify(field.options || [])});`);
          if (field.required) {
            lines.push(`  ${baseVar}.setRequired(true);`);
          }
          break;

        case 'date':
          lines.push(`  const ${baseVar} = form.addDateItem();`);
          lines.push(`  ${baseVar}.setTitle(${toJsString(title)});`);
          if (field.required) {
            lines.push(`  ${baseVar}.setRequired(true);`);
          }
          break;

        case 'file':
          lines.push(`  const ${baseVar} = form.addFileUploadItem();`);
          lines.push(`  ${baseVar}.setTitle(${toJsString(title)});`);
          if (field.required) {
            lines.push(`  ${baseVar}.setRequired(true);`);
          }
          if (field.fieldPlaceholder?.trim()) {
            lines.push(`  ${baseVar}.setHelpText(${toJsString(field.fieldPlaceholder)});`);
          }
          lines.push('  // File upload exige conta Google Workspace e permissões adequadas.');
          break;
      }

      if (field.validation?.message?.trim()) {
        lines.push(`  ${baseVar}.setHelpText(${toJsString(field.validation.message)});`);
      }

      if (field.conditional) {
        lines.push(`  // Regra condicional original não foi convertida automaticamente: ${toJsString(`${field.conditional.field} ${field.conditional.operator} ${String(field.conditional.value ?? '')}`)};`);
      }

      if (field.events && Object.keys(field.events).length > 0) {
        lines.push('  // Eventos customizados da AIDA Forms não têm equivalente direto no Google Forms.');
      }

      lines.push('');
    });

    if (formState.theme.customCSS?.trim()) {
      lines.push('  // Observação: custom CSS da AIDA Forms não é suportado em Google Forms.');
      lines.push('');
    }

    lines.push("  Logger.log('Form URL: ' + form.getPublishedUrl());");
    lines.push("  Logger.log('Edit URL: ' + form.getEditUrl());");
    lines.push("  Logger.log('Responses sheet: ' + responseSheet.getUrl());");
    lines.push('}');

    return lines.join('\n');
  };

  const handleCopyAppsScript = async () => {
    await navigator.clipboard.writeText(buildAppsScript());
    toast.success(tt('formBuilder.menu.appsScriptCopied', 'Apps Script copiado'));
  };

  const selectedField = formState.fields.find((f) => f.id === selectedFieldId);
  const showRightPanel = activeTab !== 'settings' && (showSettings || !!selectedField);

  const toggleSettingsSection = (section: keyof typeof settingsSections) => {
    setSettingsSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const updateSettingsUi = (updates: Partial<typeof settingsUi>) => {
    setSettingsUi((prev) => ({ ...prev, ...updates }));
  };

  const renderSwitch = (checked: boolean, onToggle: () => void, disabled = false) => (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-brand-600' : 'bg-slate-300 dark:bg-gray-700'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );

  const quickInsert = [
    { type: 'radio' as const, icon: Circle },
    { type: 'checkbox' as const, icon: CheckSquare },
    { type: 'text' as const, icon: Type },
    { type: 'select' as const, icon: Plus },
    { type: 'file' as const, icon: FileUp },
  ];
  const themePresets = [
    { id: 't-systems', name: tt('formBuilder.themePanel.presetNames.tsystems', 'T-SYSTEMS'), primary: '#e20074', secondary: '#7f123f', fontFamily: 'inherit' },
    { id: 'magenta', name: tt('formBuilder.themePanel.presetNames.magenta', 'Magenta'), primary: '#c70065', secondary: '#4a0d2a', fontFamily: "'Inter', sans-serif" },
    { id: 'sunset', name: tt('formBuilder.themePanel.presetNames.sunset', 'Coral'), primary: '#f07ab0', secondary: '#8c2f45', fontFamily: "'Poppins', sans-serif" },
    { id: 'forest', name: tt('formBuilder.themePanel.presetNames.forest', 'Plum'), primary: '#8a1f4d', secondary: '#3d0b23', fontFamily: "'Open Sans', sans-serif" },
    { id: 'rose', name: tt('formBuilder.themePanel.presetNames.rose', 'Rose'), primary: '#ff5fa2', secondary: '#a83264', fontFamily: "'Montserrat', sans-serif" },
  ];
  const fontOptions = [
    { value: 'inherit', label: tt('formBuilder.themePanel.fontDefault', 'AIDA Forms Default') },
    { value: "'Inter', sans-serif", label: 'Inter' },
    { value: "'Roboto', sans-serif", label: 'Roboto' },
    { value: "'Poppins', sans-serif", label: 'Poppins' },
    { value: "'Montserrat', sans-serif", label: 'Montserrat' },
    { value: "'Open Sans', sans-serif", label: 'Open Sans' },
  ];

  const shortcutsSections = [
    {
      title: tt('formBuilder.shortcuts.sections.navigation', 'Navigation'),
      items: [
        { label: tt('formBuilder.shortcuts.items.moveCursorUp', 'Move cursor up'), keys: ['Ctrl', 'K'] },
        { label: tt('formBuilder.shortcuts.items.moveCursorDown', 'Move cursor down'), keys: ['Ctrl', 'J'] },
        { label: tt('formBuilder.shortcuts.items.viewShortcuts', 'View keyboard shortcuts'), keys: ['Ctrl', '/'] },
        { label: tt('formBuilder.shortcuts.items.focusPopup', 'Focus popup'), keys: ['Ctrl', 'Shift', '0'] },
        { label: tt('formBuilder.shortcuts.items.prevResponse', 'View previous response'), keys: ['Ctrl', 'Shift', '←'] },
        { label: tt('formBuilder.shortcuts.items.nextResponse', 'View next response'), keys: ['Ctrl', 'Shift', '→'] },
      ],
    },
    {
      title: tt('formBuilder.shortcuts.sections.fileCommands', 'File commands'),
      items: [
        { label: tt('formBuilder.shortcuts.items.previewForm', 'Preview form'), keys: ['Ctrl', 'Shift', 'P'] },
        { label: tt('formBuilder.shortcuts.items.publishForm', 'Publish form'), keys: ['Ctrl', 'Enter'] },
        { label: tt('formBuilder.shortcuts.items.print', 'Print'), keys: ['Ctrl', 'P'] },
      ],
    },
    {
      title: tt('formBuilder.shortcuts.sections.menus', 'Menus'),
      items: [
        { label: tt('formBuilder.shortcuts.items.formSettings', 'Form settings'), keys: ['Ctrl', 'E'] },
        { label: tt('formBuilder.shortcuts.items.yourAddons', 'Your add-ons'), keys: ['Alt', 'Shift', 'N'] },
        { label: tt('formBuilder.shortcuts.items.more', 'More'), keys: ['Alt', 'Shift', 'S'] },
        { label: tt('formBuilder.shortcuts.items.openThemeDialog', 'Open the theme dialog'), keys: ['Alt', 'Shift', 'T'] },
      ],
    },
    {
      title: tt('formBuilder.shortcuts.sections.formActions', 'Form actions'),
      items: [
        { label: tt('formBuilder.shortcuts.items.addQuestion', 'Add question'), keys: ['Ctrl', 'Shift', 'Enter'], alt: ['Ctrl', 'I'] },
        { label: tt('formBuilder.shortcuts.items.addTitleDescription', 'Add title and description'), keys: ['Ctrl', 'I', 'Ctrl', 'H'] },
        { label: tt('formBuilder.shortcuts.items.addImage', 'Add image'), keys: ['Ctrl', 'I', 'Ctrl', 'P'] },
        { label: tt('formBuilder.shortcuts.items.addVideo', 'Add video'), keys: ['Ctrl', 'I', 'Ctrl', 'V'] },
        { label: tt('formBuilder.shortcuts.items.addSection', 'Add section'), keys: ['Ctrl', 'I', 'Ctrl', 'B'] },
        { label: tt('formBuilder.shortcuts.items.moveItemUp', 'Move item up'), keys: ['Ctrl', 'Shift', 'K'] },
        { label: tt('formBuilder.shortcuts.items.moveItemDown', 'Move item down'), keys: ['Ctrl', 'Shift', 'J'] },
        { label: tt('formBuilder.shortcuts.items.duplicateItem', 'Duplicate item'), keys: ['Ctrl', 'Shift', 'D'] },
        { label: tt('formBuilder.shortcuts.items.deleteItem', 'Delete item'), keys: ['Alt', 'Shift', 'D'] },
      ],
    },
    {
      title: tt('formBuilder.shortcuts.sections.mediaAlignment', 'Media alignment'),
      items: [
        { label: tt('formBuilder.shortcuts.items.alignLeft', 'Align left'), keys: ['Ctrl', 'Shift', 'L'] },
        { label: tt('formBuilder.shortcuts.items.alignCenter', 'Align center'), keys: ['Ctrl', 'Shift', 'E'] },
        { label: tt('formBuilder.shortcuts.items.alignRight', 'Align right'), keys: ['Ctrl', 'Shift', 'R'] },
      ],
    },
    {
      title: tt('formBuilder.shortcuts.sections.editing', 'Editing'),
      items: [
        { label: tt('formBuilder.shortcuts.items.undo', 'Undo'), keys: ['Ctrl', 'Z'] },
        { label: tt('formBuilder.shortcuts.items.redo', 'Redo'), keys: ['Ctrl', 'Y'], alt: ['Ctrl', 'Shift', 'Z'] },
        { label: tt('formBuilder.shortcuts.items.moveOptionUp', 'Move option up'), keys: ['Ctrl', 'Alt', '↑'] },
        { label: tt('formBuilder.shortcuts.items.moveOptionDown', 'Move option down'), keys: ['Ctrl', 'Alt', '↓'] },
      ],
    },
  ];

  const filteredShortcutsSections = shortcutsSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        item.label.toLowerCase().includes(shortcutsQuery.toLowerCase())
      ),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="min-h-screen bg-[#f6f4f7] dark:bg-[linear-gradient(180deg,#17131a_0%,#1d191f_100%)]">
      <Sidebar
        collapsed={false}
        onToggle={() => setSidebarOpen((prev) => !prev)}
        isMobile={true}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <header className="flex h-16 flex-shrink-0 items-center justify-between gap-2 overflow-hidden border-b border-slate-200 bg-white px-3 dark:border-gray-800 dark:bg-gray-900">
            {/* LEFT — hamburger + logo + divider + back arrow + title */}
            <div className="flex min-w-0 shrink items-center gap-2">
              <button
                onClick={() => setSidebarOpen((prev) => !prev)}
                className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-600 dark:text-slate-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400"
                title={tt('header.menu', 'Menu')}
              >
                <MenuIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <AidaFormsLogo size="lg" />

              {/* Divider */}
              <div className="mx-1 h-6 w-px bg-slate-200 dark:bg-gray-700" />

              {/* Back arrow */}
              <button
                onClick={() => navigate('/forms')}
                className="flex-shrink-0 rounded-md border border-brand-200 bg-brand-50 px-2 py-1.5 text-brand-600 transition hover:border-brand-400 hover:bg-brand-100 dark:border-brand-700 dark:bg-brand-500/10 dark:text-brand-300 dark:hover:bg-brand-500/20"
                title={tt('formBuilder.actions.backToForms', 'Voltar para Formulários')}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              {/* Title input */}
              <input
                type="text"
                value={formState.formTitle}
                onChange={(e) => handleFormSettingsUpdate({ formTitle: e.target.value })}
                className="h-9 w-[180px] max-w-[22vw] rounded-xl bg-transparent px-3 text-sm font-semibold text-slate-900 outline-none transition hover:bg-brand-50 focus:bg-brand-50 focus:ring-1 focus:ring-brand-400 dark:text-gray-100 dark:hover:bg-brand-500/10 dark:focus:bg-brand-500/10"
                placeholder={tt('formBuilder.placeholders.title', 'Untitled form')}
              />

              {/* Star/Favorite */}
              <button
                onClick={() => setIsFavorite((prev) => !prev)}
                className={`rounded-md border px-2 py-1.5 transition ${
                  isFavorite
                    ? 'border-brand-300 bg-brand-50 text-brand-600 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-300'
                    : 'border-slate-200 bg-slate-50 text-slate-400 hover:text-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-500 dark:hover:text-brand-400'
                }`}
                title={isFavorite ? tt('formBuilder.actions.unfavorite', 'Remover favorito') : tt('formBuilder.actions.favorite', 'Favoritar')}
              >
                <Star className={`h-3.5 w-3.5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
            </div>

            {/* CENTER — toolbar */}
            <div className="flex min-w-0 flex-1 items-center justify-center overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex min-w-0 items-center gap-1">

                {/* Group: Undo / Redo */}
                <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-900">
                  <button onClick={() => toast(tt('formBuilder.actions.undoSoon', 'Histórico em breve'))} className="rounded-l-md px-2 py-1.5 text-slate-500 transition hover:bg-brand-50 hover:text-brand-600 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400" title={tt('formBuilder.actions.undo', 'Desfazer')}>
                    <Undo2 className="h-3.5 w-3.5" />
                  </button>
                  <div className="h-4 w-px bg-brand-200 dark:bg-brand-800" />
                  <button onClick={() => toast(tt('formBuilder.actions.redoSoon', 'Histórico em breve'))} className="rounded-r-md px-2 py-1.5 text-slate-500 transition hover:bg-brand-50 hover:text-brand-600 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400" title={tt('formBuilder.actions.redo', 'Refazer')}>
                    <Redo2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Group: Preview modes */}
                <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-900">
                  {[
                    { mode: 'desktop' as const, icon: Monitor },
                    { mode: 'tablet' as const, icon: Tablet },
                    { mode: 'mobile' as const, icon: Smartphone },
                  ].map(({ mode, icon: ModeIcon }, i, arr) => (
                    <React.Fragment key={mode}>
                      <button
                        onClick={() => { setPreviewMode(mode); setShowPreview(true); }}
                        className={`px-2 py-1.5 transition ${previewMode === mode && showPreview ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300' : 'text-slate-400 hover:bg-brand-50 hover:text-brand-600 dark:text-gray-500 dark:hover:bg-brand-500/10 dark:hover:text-brand-400'}`}
                        title={mode.charAt(0).toUpperCase() + mode.slice(1)}
                      >
                        <ModeIcon className="h-3.5 w-3.5" />
                      </button>
                      {i < arr.length - 1 && <div className="h-4 w-px bg-brand-200 dark:bg-brand-800" />}
                    </React.Fragment>
                  ))}
                </div>

                {/* Group: Theme + Link + Embed */}
                <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-900">
                  <button onClick={() => setShowThemePanel(true)} className="rounded-l-md px-2 py-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-brand-600 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-brand-400" title={tt('formBuilder.actions.theme', 'Tema')}>
                    <Palette className="h-3.5 w-3.5" />
                  </button>
                  <div className="h-4 w-px bg-slate-200 dark:bg-gray-700" />
                  <button onClick={handleCopyPublicLink} className="px-2 py-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-brand-600 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-brand-400" title={tt('formBuilder.actions.copyLink', 'Copiar link público')}>
                    <Link2 className="h-3.5 w-3.5" />
                  </button>
                  <div className="h-4 w-px bg-slate-200 dark:bg-gray-700" />
                  <button onClick={handleEmbedHtml} className="rounded-r-md px-2 py-1.5 text-slate-400 transition hover:bg-brand-50 hover:text-brand-600 dark:text-gray-500 dark:hover:bg-brand-500/10 dark:hover:text-brand-400" title={tt('formBuilder.menu.embedHtml', 'Incorporar HTML')}>
                    <Code2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Group: Collaborators + Share */}
                <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-900">
                  <button onClick={() => setShowShareModal(true)} className="rounded-l-md px-2 py-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-brand-600 dark:text-gray-500 dark:hover:bg-gray-800" title={tt('formBuilder.actions.collaborators', 'Colaboradores')}>
                    <UserPlus className="h-3.5 w-3.5" />
                  </button>
                  <div className="h-4 w-px bg-slate-200 dark:bg-gray-700" />
                  <button onClick={() => setShowAppsScriptModal(true)} className="rounded-r-md px-2 py-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-orange-600 dark:text-gray-500 dark:hover:bg-gray-800" title={tt('formBuilder.actions.appsScript', 'Apps Script')}>
                    <Puzzle className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Publish button */}
                <button
                  onClick={handleSave}
                  className="flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700"
                >
                  {tt('formBuilder.actions.publish', 'Salvar')}
                </button>

                {/* More menu */}
                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-slate-500 transition hover:bg-slate-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800">
                    <MoreVertical className="h-3.5 w-3.5" />
                  </Menu.Button>
                    <Menu.Items className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-2xl dark:border-gray-800 dark:bg-gray-900">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleDuplicateForm}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm ${active ? 'bg-slate-50 dark:bg-gray-800' : ''}`}
                      >
                        <Copy className="h-4.5 w-4.5 text-slate-500" />
                        <span>{tt('formBuilder.menu.makeCopy', 'Fazer uma cópia')}</span>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {() => (
                      <button
                        disabled
                        className="flex w-full cursor-not-allowed items-center gap-3 px-4 py-3 text-left text-sm text-slate-300 dark:text-slate-600"
                      >
                        <Trash2 className="h-4.5 w-4.5" />
                        <span>{tt('formBuilder.menu.moveToTrash', 'Mover para a lixeira')}</span>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleAutofillTemplate}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm ${active ? 'bg-slate-50 dark:bg-gray-800' : ''}`}
                      >
                        <FilePenLine className="h-4.5 w-4.5 text-slate-500" />
                        <span>{tt('formBuilder.menu.autofill', 'Preencher formulário automaticamente')}</span>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleEmbedHtml}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm ${active ? 'bg-slate-50 dark:bg-gray-800' : ''}`}
                      >
                        <Code2 className="h-4.5 w-4.5 text-slate-500" />
                        <span>{tt('formBuilder.menu.embedHtml', 'Incorporar HTML')}</span>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => window.print()}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm ${active ? 'bg-slate-50 dark:bg-gray-800' : ''}`}
                      >
                        <Printer className="h-4.5 w-4.5 text-slate-500" />
                        <span>{tt('formBuilder.menu.print', 'Imprimir')}</span>
                      </button>
                    )}
                  </Menu.Item>
                  <div className="my-2 border-t border-slate-200 dark:border-gray-800" />
                  <Menu.Item>
                    {() => (
                      <button
                        disabled
                        className="flex w-full cursor-not-allowed items-center gap-3 px-4 py-3 text-left text-sm text-slate-300 dark:text-slate-600"
                      >
                        <X className="h-4.5 w-4.5" />
                        <span>{tt('formBuilder.menu.unpublish', 'Cancelar publicação do formulário')}</span>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => setShowShortcutsModal(true)}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm ${active ? 'bg-slate-50 dark:bg-gray-800' : ''}`}
                      >
                        <Keyboard className="h-4.5 w-4.5 text-slate-500" />
                        <span>{tt('formBuilder.menu.shortcuts', 'Atalhos do teclado')}</span>
                      </button>
                    )}
                  </Menu.Item>
                    </Menu.Items>
                  </Menu>

              </div>
            </div>

            {/* RIGHT — language, theme, notifications, user */}
            <div className="ml-auto flex shrink-0 items-center gap-3 pr-1">
              <LanguageSwitcher />
              <ThemeSelector />
              <NotificationDropdown />
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-2.5 py-1.5 transition hover:border-slate-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-slate-600">
                  <Avatar src={user?.photo?.path} fallback={currentUserInitial} size="sm" />
                  <div className="hidden text-left 2xl:block">
                    <div className="max-w-[140px] truncate text-sm font-medium text-slate-900 dark:text-white">
                      {currentUserName}
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </Menu.Button>
                <Menu.Items className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-2xl dark:border-gray-800 dark:bg-gray-900">
                  <div className="border-b border-slate-200 px-4 py-3 dark:border-gray-800">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{currentUserName}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">{currentUserEmail}</p>
                  </div>
                  <Menu.Item>
                    {({ active }) => (
                      <button onClick={() => navigate('/dashboard')} className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm ${active ? 'bg-slate-50 dark:bg-gray-800' : ''}`}>
                        <LayoutPanelLeft className="h-4.5 w-4.5 text-slate-500" />
                        <span>{tt('formBuilder.actions.backToStudio', 'Voltar ao AIDA Forms')}</span>
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button onClick={logout} className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-rose-600 dark:text-rose-400 ${active ? 'bg-rose-50 dark:bg-rose-950/30' : ''}`}>
                        <LogOut className="h-4.5 w-4.5" />
                        <span>{tt('header.logout', 'Log out')}</span>
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Menu>
            </div>
        </header>

        <div className="border-b border-slate-200 bg-white dark:border-gray-800 dark:bg-gray-900">
          <div className="mx-auto flex max-w-[980px] items-center justify-center gap-8 px-4">
            <button
              onClick={() => {
                setActiveTab('questions');
                setShowSettings(false);
              }}
              className={`border-b-2 px-2 py-4 text-sm font-medium transition ${
                activeTab === 'questions'
                  ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              {tt('formBuilder.tabs.questions', 'Perguntas')}
            </button>
            <button
              onClick={() => {
                setActiveTab('responses');
                setShowPreview(false);
                setShowSettings(false);
                setSelectedFieldId(null);
              }}
              className={`border-b-2 px-2 py-4 text-sm font-medium transition ${
                activeTab === 'responses'
                  ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              {tt('formBuilder.tabs.responses', 'Respostas')}
            </button>
            <button
              onClick={() => {
                setActiveTab('settings');
                setShowSettings(true);
                setSelectedFieldId(null);
              }}
              className={`border-b-2 px-2 py-4 text-sm font-medium transition ${
                activeTab === 'settings'
                  ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              {tt('formBuilder.tabs.settings', 'Configurações')}
            </button>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-none gap-4 px-3 py-4">
          {showPalette && (
            <aside className="hidden w-[280px] shrink-0 lg:block">
              <FieldPalette />
            </aside>
          )}

          <main className="min-w-0 flex-1">
            <div className="relative">
              <div className="mx-auto flex w-full max-w-[1080px] items-start gap-4">
            <div className="pointer-events-none sticky top-24 z-20 hidden shrink-0 flex-col md:flex">
              {activeTab === 'questions' && (
                <div className="pointer-events-auto flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg dark:border-gray-800 dark:bg-gray-900">
                  {quickInsert.map(({ type, icon: Icon }) => (
                    <button
                      key={type}
                      onClick={() => addField(type)}
                      className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500 transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-slate-600 dark:hover:bg-gray-700 dark:hover:text-white"
                      title={getFieldTypeLabel(type)}
                    >
                      <Icon className="h-4.5 w-4.5" />
                    </button>
                  ))}
                  <div className="mx-1 my-1 h-px bg-slate-200 dark:bg-gray-800" />
                  <button
                    onClick={() => setShowPalette((prev) => !prev)}
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500 transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-slate-600 dark:hover:bg-gray-700 dark:hover:text-white"
                    title={tt('formBuilder.actions.blocks', 'Blocks')}
                  >
                    <LayoutPanelLeft className="h-4.5 w-4.5" />
                  </button>
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
                {activeTab === 'questions' && (
                  <>
                    <div className="mb-4 overflow-hidden rounded-[12px] border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                      <div
                        className="h-3"
                        style={{
                          background: `linear-gradient(90deg, ${formState.theme.primaryColor || '#e20074'}, ${formState.theme.secondaryColor || '#7f123f'})`,
                        }}
                      />
                      <div className="p-6">
                        <textarea
                          value={formState.formTitle}
                          onChange={(e) => handleFormSettingsUpdate({ formTitle: e.target.value })}
                          className="w-full resize-none border-none bg-transparent p-0 text-[clamp(1.35rem,4.2vw,2.5rem)] font-normal tracking-tight text-slate-950 outline-none placeholder:text-slate-300 dark:text-white dark:placeholder:text-slate-600"
                          rows={1}
                          placeholder={tt('formBuilder.placeholders.title', 'Untitled form')}
                        />
                        <textarea
                          value={formState.formDescription}
                          onChange={(e) => handleFormSettingsUpdate({ formDescription: e.target.value })}
                          className="mt-3 w-full resize-none border-none border-t border-slate-200 bg-transparent p-0 pt-3 text-sm leading-7 text-slate-500 outline-none placeholder:text-slate-300 dark:border-gray-800 dark:text-gray-400 dark:placeholder:text-slate-600"
                          rows={2}
                          placeholder={tt('formBuilder.placeholders.description', 'Add a short introduction so people know what this form is for.')}
                        />
                      </div>
                    </div>

                    <SortableContext items={formState.fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                      <FormCanvas
                        fields={formState.fields}
                        selectedFieldId={selectedFieldId}
                        onFieldSelect={setSelectedFieldId}
                        onFieldDelete={handleFieldDelete}
                        onFieldDuplicate={handleFieldDuplicate}
                      />
                    </SortableContext>
                  </>
                )}

                {activeTab === 'responses' && (
                  <div className="space-y-4">
                    <div className="overflow-hidden rounded-[12px] border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                      <div className="h-3 bg-gradient-to-r from-brand-500 to-brand-700" />
                      <div className="p-6">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                            <BarChart3 className="h-5 w-5" />
                          </div>
                          <div>
                            <h2 className="text-2xl font-semibold text-slate-950 dark:text-white">
                              {tt('formBuilder.responses.title', 'Responses')}
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-gray-400">
                              {tt('formBuilder.responses.description', 'Track submissions, completion rate, and field performance here.')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="rounded-[12px] border border-slate-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                        <div className="text-sm text-slate-500 dark:text-gray-400">
                          {tt('formBuilder.responses.cards.total', 'Total submissions')}
                        </div>
                        <div className="mt-3 text-3xl font-semibold text-slate-950 dark:text-white">
                          {submissionsLoading ? '…' : submissions.length}
                        </div>
                      </div>
                      <div className="rounded-[12px] border border-slate-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                        <div className="text-sm text-slate-500 dark:text-gray-400">
                          {tt('formBuilder.responses.cards.completion', 'Completion rate')}
                        </div>
                        <div className="mt-3 text-3xl font-semibold text-slate-950 dark:text-white">
                          {submissions.length > 0 ? '100%' : '0%'}
                        </div>
                      </div>
                      <div className="rounded-[12px] border border-slate-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                        <div className="text-sm text-slate-500 dark:text-gray-400">
                          {tt('formBuilder.responses.cards.lastResponse', 'Last response')}
                        </div>
                        <div className="mt-3 text-lg font-semibold text-slate-950 dark:text-white">
                          {submissions.length > 0
                            ? new Date(submissions[0].submittedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                            : tt('formBuilder.responses.none', 'Nenhuma resposta ainda')}
                        </div>
                      </div>
                    </div>

                    {submissionsLoading ? (
                      <div className="flex justify-center py-12 text-sm text-slate-400">{tt('formBuilder.responses.loading', 'Loading responses…')}</div>
                    ) : submissions.length === 0 ? (
                      <div className="rounded-[12px] border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-gray-300">
                          <BarChart3 className="h-6 w-6" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-950 dark:text-white">
                          {tt('formBuilder.responses.emptyTitle', 'Ainda não há envios')}
                        </h3>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-slate-500 dark:text-gray-400">
                          {tt('formBuilder.responses.emptyDescription', 'Quando as pessoas começarem a enviar este formulário, o resumo de respostas aparecerá aqui.')}
                        </p>
                      </div>
                    ) : (
                      <div className="rounded-[12px] border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-800/50">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400">#</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400">{tt('formBuilder.responses.dateTimeHeader', 'Date/Time')}</th>
                                {Object.keys(submissions[0]?.data || {}).map(k => (
                                  <th key={k} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 max-w-[200px] truncate">{k}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {submissions.map((s, i) => (
                                <tr key={s.id} className="border-b border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800/40">
                                  <td className="px-4 py-3 text-slate-400 text-xs">{i + 1}</td>
                                  <td className="px-4 py-3 text-slate-600 dark:text-gray-400 text-xs break-words">
                                    {new Date(s.submittedAt).toLocaleString('pt-BR')}
                                  </td>
                                  {Object.keys(submissions[0]?.data || {}).map(k => (
                                    <td key={k} className="px-4 py-3 text-slate-800 dark:text-gray-200 max-w-[200px] truncate">
                                      {String(s.data[k] ?? '—')}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'settings' && (
                  <div className="space-y-4">
                    <div className="rounded-[12px] border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                      <div className="border-b border-slate-200 px-6 py-5 dark:border-gray-800">
                        <h2 className="break-words text-[clamp(1.35rem,4vw,2rem)] font-normal text-slate-950 dark:text-white">
                          {tt('formBuilder.settingsPage.title', 'Configurações')}
                        </h2>
                      </div>

                      <div className="px-6">
                        <div className="flex items-center justify-between border-b border-slate-200 py-8 dark:border-gray-800">
                          <div>
                            <h3 className="break-words text-[clamp(1.15rem,3vw,1.5rem)] font-normal text-slate-950 dark:text-white">
                              {tt('formBuilder.settingsPage.quiz.title', 'Criar teste')}
                            </h3>
                            <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                              {tt('formBuilder.settingsPage.quiz.description', 'Atribua pontuações, defina as respostas e dê feedback automaticamente.')}
                            </p>
                          </div>
                          {renderSwitch(false, () => undefined, true)}
                        </div>

                        <div className="border-b border-slate-200 py-7 dark:border-gray-800">
                          <button
                            onClick={() => toggleSettingsSection('responses')}
                            className="flex w-full items-center justify-between gap-4 text-left"
                          >
                            <div>
                              <h3 className="break-words text-[clamp(1.15rem,3vw,1.5rem)] font-normal text-slate-950 dark:text-white">
                                {tt('formBuilder.settingsPage.responses.title', 'Respostas')}
                              </h3>
                              <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                                {tt('formBuilder.settingsPage.responses.description', 'Gerenciar como as respostas são coletadas e protegidas')}
                              </p>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-slate-500 transition ${settingsSections.responses ? 'rotate-180' : ''}`} />
                          </button>
                          {settingsSections.responses && (
                            <div className="mt-5 space-y-5 rounded-[12px] bg-slate-50 p-5 dark:bg-gray-900">
                              <div className="grid gap-5 md:grid-cols-[1fr_180px] md:items-center">
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                                    {tt('formBuilder.settingsPage.responses.collectEmail', 'Coletar endereços de e-mail')}
                                  </div>
                                  <div className="text-sm text-slate-500 dark:text-gray-400">
                                    {tt('formBuilder.settingsPage.responses.collectEmailDescription', 'Adiciona campo de e-mail obrigatório ao formulário.')}
                                  </div>
                                </div>
                                <select
                                  value={formState.config.collectEmailMode || 'none'}
                                  onChange={(e) => handleFormSettingsUpdate({ config: { ...formState.config, collectEmailMode: e.target.value as 'none' | 'manual' } })}
                                  className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                                >
                                  <option value="none">{tt('formBuilder.settingsPage.responses.collectEmailNone', 'Não coletar')}</option>
                                  <option value="manual">{tt('formBuilder.settingsPage.responses.collectEmailManual', 'Coletar e-mail')}</option>
                                </select>
                              </div>

                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                                    {tt('formBuilder.settingsPage.responses.limitOne', 'Limitar a 1 resposta por IP')}
                                  </div>
                                  <div className="text-sm text-slate-500 dark:text-gray-400">
                                    {tt('formBuilder.settingsPage.responses.limitOneDescription', 'Bloqueia reenvio do mesmo endereço IP.')}
                                  </div>
                                </div>
                                {renderSwitch(
                                  !formState.config.allowMultipleSubmissions,
                                  () => handleFormSettingsUpdate({ config: { ...formState.config, allowMultipleSubmissions: !formState.config.allowMultipleSubmissions } })
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="border-b border-slate-200 py-7 dark:border-gray-800">
                          <button
                            onClick={() => toggleSettingsSection('presentation')}
                            className="flex w-full items-center justify-between gap-4 text-left"
                          >
                            <div>
                              <h3 className="break-words text-[clamp(1.15rem,3vw,1.5rem)] font-normal text-slate-950 dark:text-white">
                                {tt('formBuilder.settingsPage.presentation.title', 'Apresentação')}
                              </h3>
                              <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                                {tt('formBuilder.settingsPage.presentation.description', 'Gerenciar como o formulário e as respostas são apresentados')}
                              </p>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-slate-500 transition ${settingsSections.presentation ? 'rotate-180' : ''}`} />
                          </button>
                          {settingsSections.presentation && (
                            <div className="mt-5 space-y-5 rounded-[12px] bg-slate-50 p-5 dark:bg-gray-900">
                              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-gray-500">
                                {tt('formBuilder.settingsPage.presentation.formSection', 'Apresentação do formulário')}
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                                    {tt('formBuilder.settingsPage.presentation.progressBar', 'Mostrar barra de progresso')}
                                  </div>
                                  <div className="text-sm text-slate-500 dark:text-gray-400">
                                    {tt('formBuilder.settingsPage.presentation.progressBarDescription', 'Gerenciar como o avanço do formulário é exibido.')}
                                  </div>
                                </div>
                                {renderSwitch(
                                  formState.config.showProgressBar,
                                  () => handleFormSettingsUpdate({
                                    config: {
                                      ...formState.config,
                                      showProgressBar: !formState.config.showProgressBar,
                                    },
                                  })
                                )}
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                                    {tt('formBuilder.settingsPage.presentation.shuffleQuestions', 'Embaralhar a ordem das perguntas')}
                                  </div>
                                  <div className="text-sm text-slate-500 dark:text-gray-400">
                                    {tt('formBuilder.settingsPage.presentation.shuffleDescription', 'Exibe as perguntas em ordem aleatória para cada participante.')}
                                  </div>
                                </div>
                                {renderSwitch(
                                  formState.config.shuffleQuestions,
                                  () => handleFormSettingsUpdate({ config: { ...formState.config, shuffleQuestions: !formState.config.shuffleQuestions } })
                                )}
                              </div>

                              <div className="pt-2">
                                <div className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-gray-500">
                                  {tt('formBuilder.settingsPage.presentation.afterSubmit', 'Após o envio')}
                                </div>
                                <div className="mb-4 flex items-start justify-between gap-4">
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-900 dark:text-white">
                                      {tt('formBuilder.settingsPage.presentation.confirmationMessage', 'Mensagem de confirmação')}
                                    </div>
                                    {!settingsUi.editingConfirmationMessage ? (
                                      <p className="mt-1 text-sm italic text-slate-500 dark:text-gray-400">
                                        {formState.successMessage || tt('formBuilder.defaults.successMessage', 'Sua resposta foi registrada.')}
                                      </p>
                                    ) : (
                                      <input
                                        type="text"
                                        value={settingsUi.confirmationDraft}
                                        onChange={(e) => updateSettingsUi({ confirmationDraft: e.target.value })}
                                        className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                                        placeholder={tt('formBuilder.defaults.successMessage', 'Sua resposta foi registrada.')}
                                      />
                                    )}
                                  </div>
                                  <div className="shrink-0">
                                    {!settingsUi.editingConfirmationMessage ? (
                                      <button
                                        onClick={() => updateSettingsUi({ editingConfirmationMessage: true, confirmationDraft: formState.successMessage })}
                                        className="text-sm font-medium text-brand-600 hover:text-brand-700"
                                      >
                                        {tt('formBuilder.settingsPage.presentation.edit', 'Editar')}
                                      </button>
                                    ) : (
                                      <div className="flex items-center gap-4">
                                        <button
                                          onClick={() => {
                                            handleFormSettingsUpdate({ successMessage: settingsUi.confirmationDraft });
                                            updateSettingsUi({ editingConfirmationMessage: false });
                                          }}
                                          className="text-sm font-medium text-brand-600 hover:text-brand-700"
                                        >
                                          {tt('actions.save', 'Salvar')}
                                        </button>
                                        <button
                                          onClick={() => updateSettingsUi({ editingConfirmationMessage: false, confirmationDraft: formState.successMessage })}
                                          className="text-sm font-medium text-slate-500 hover:text-slate-700"
                                        >
                                          {tt('actions.cancel', 'Cancelar')}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-[12px] border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                      <div className="border-b border-slate-200 px-6 py-5 dark:border-gray-800">
                        <h2 className="break-words text-[clamp(1.35rem,4vw,2rem)] font-normal text-slate-950 dark:text-white">
                          {tt('formBuilder.settingsPage.defaults.title', 'Padrões')}
                        </h2>
                      </div>

                      <div className="px-6">
                        <div className="border-b border-slate-200 py-7 dark:border-gray-800">
                          <button
                            onClick={() => toggleSettingsSection('formDefaults')}
                            className="flex w-full items-center justify-between gap-4 text-left"
                          >
                            <div>
                              <h3 className="break-words text-[clamp(1.15rem,3vw,1.5rem)] font-normal text-slate-950 dark:text-white">
                                {tt('formBuilder.settingsPage.defaults.form.title', 'Configurações padrão dos formulários')}
                              </h3>
                              <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                                {tt('formBuilder.settingsPage.defaults.form.description', 'Configurações aplicadas a este e a novos formulários')}
                              </p>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-slate-500 transition ${settingsSections.formDefaults ? 'rotate-180' : ''}`} />
                          </button>
                          {settingsSections.formDefaults && (
                            <div className="mt-5 grid gap-5 rounded-[12px] bg-slate-50 p-5 dark:bg-gray-900 md:grid-cols-[1fr_220px] md:items-center">
                              <div>
                                <div className="text-sm font-medium text-slate-900 dark:text-white">
                                  {tt('formBuilder.settingsPage.defaults.form.collectEmailDefault', 'Coletar endereços de e-mail por padrão')}
                                </div>
                              </div>
                              <select
                                value={settingsUi.defaultCollectEmailMode}
                                onChange={(e) => updateSettingsUi({ defaultCollectEmailMode: e.target.value })}
                                className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                              >
                                <option value="none">{tt('formBuilder.settingsPage.responses.collectEmailNone', 'Não coletar')}</option>
                                <option value="verified">{tt('formBuilder.settingsPage.responses.collectEmailVerified', 'Coletar verificado')}</option>
                                <option value="manual">{tt('formBuilder.settingsPage.responses.collectEmailManual', 'Coletar manualmente')}</option>
                              </select>
                            </div>
                          )}
                        </div>

                        <div className="py-7">
                          <button
                            onClick={() => toggleSettingsSection('questionDefaults')}
                            className="flex w-full items-center justify-between gap-4 text-left"
                          >
                            <div>
                              <h3 className="break-words text-[clamp(1.15rem,3vw,1.5rem)] font-normal text-slate-950 dark:text-white">
                                {tt('formBuilder.settingsPage.defaults.question.title', 'Configurações padrão das perguntas')}
                              </h3>
                              <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                                {tt('formBuilder.settingsPage.defaults.question.description', 'Configurações aplicadas a todas as novas perguntas')}
                              </p>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-slate-500 transition ${settingsSections.questionDefaults ? 'rotate-180' : ''}`} />
                          </button>
                          {settingsSections.questionDefaults && (
                            <div className="mt-5 space-y-5 rounded-[12px] bg-slate-50 p-5 dark:bg-gray-900">
                              <div className="flex items-center justify-between gap-4">
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                                    {tt('formBuilder.settingsPage.defaults.question.required', 'Tornar as perguntas obrigatórias por padrão')}
                                  </div>
                                </div>
                                {renderSwitch(settingsUi.defaultQuestionsRequired, () => {
                                  const next = !settingsUi.defaultQuestionsRequired;
                                  updateSettingsUi({ defaultQuestionsRequired: next });
                                  setFormState((prev) => ({
                                    ...prev,
                                    fields: prev.fields.map((field) => ({
                                      ...field,
                                      required: next,
                                    })),
                                  }));
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
            </div>
          </main>

          {showRightPanel && (
            <aside className="hidden w-[320px] shrink-0 xl:block 2xl:w-[360px]">
              <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                {showSettings ? (
                  <FormSettingsPanel
                    formState={formState}
                    onUpdate={handleFormSettingsUpdate}
                    onClose={() => setShowSettings(false)}
                  />
                ) : selectedField ? (
                  <FieldPropertiesPanel
                    field={selectedField}
                    allFields={formState.fields}
                    onUpdate={(updates) => handleFieldUpdate(selectedField.id, updates)}
                    onClose={() => setSelectedFieldId(null)}
                  />
                ) : null}
              </div>
            </aside>
          )}
        </div>

        <DragOverlay>
          {activeId ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:text-white">
              {activeId.toString().startsWith('palette-')
                ? tt('formBuilder.overlay.newField', 'New field')
                : formState.fields.find((f) => f.id === activeId)?.fieldLabel}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm">
          <div className="flex h-full flex-col">
            <div className="border-b border-slate-200 bg-white px-6 py-3 dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setShowPreview(false)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-gray-300 dark:hover:bg-gray-900 dark:hover:text-white"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <h3 className="text-[18px] font-medium text-slate-900 dark:text-white">
                    {tt('formBuilder.preview.modeTitle', 'Modo de visualização')}
                  </h3>
                </div>

                <div className="flex items-center gap-3">
                  <div className="hidden items-center gap-2 text-sm text-slate-500 md:flex dark:text-gray-300">
                    <Eye className="h-4 w-4" />
                    <span>{tt('formBuilder.preview.unpublished', 'Não publicado')}</span>
                  </div>
                  <button
                    onClick={handleCopyPublicLink}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-brand-600 transition hover:bg-slate-50 dark:border-gray-700 dark:text-brand-300 dark:hover:bg-gray-900"
                  >
                    <Link2 className="h-4 w-4" />
                    {tt('formBuilder.preview.copyParticipantLink', 'Copiar link do participante')}
                  </button>
                  <div className="flex items-center rounded-2xl bg-slate-100 p-1 dark:bg-gray-900">
                  <button
                    onClick={() => setPreviewMode('desktop')}
                    className={`rounded-xl p-2 transition ${previewMode === 'desktop' ? 'bg-white text-slate-900 shadow-sm dark:bg-gray-800 dark:text-white' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}
                    title={tt('formBuilder.preview.desktop', 'Desktop layout')}
                  >
                    <Monitor className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPreviewMode('tablet')}
                    className={`rounded-xl p-2 transition ${previewMode === 'tablet' ? 'bg-white text-slate-900 shadow-sm dark:bg-gray-800 dark:text-white' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}
                    title={tt('formBuilder.preview.tablet', 'Tablet layout')}
                  >
                    <Tablet className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPreviewMode('mobile')}
                    className={`rounded-xl p-2 transition ${previewMode === 'mobile' ? 'bg-white text-slate-900 shadow-sm dark:bg-gray-800 dark:text-white' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}
                    title={tt('formBuilder.preview.mobile', 'Mobile layout')}
                  >
                    <Smartphone className="h-4 w-4" />
                  </button>
                  </div>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-gray-900 dark:hover:text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between rounded-2xl bg-brand-50 px-5 py-4 text-sm text-slate-700 dark:bg-brand-500/10 dark:text-gray-200">
                <div className="flex items-center gap-3">
                  <X className="h-4 w-4 text-brand-500" />
                  <span>{tt('formBuilder.preview.responsesClosed', 'Este formulário não está aceitando respostas.')}</span>
                </div>
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setActiveTab('settings');
                    setShowSettings(false);
                  }}
                  className="font-medium text-slate-600 hover:text-slate-900 dark:text-gray-300 dark:hover:text-white"
                >
                  {tt('formBuilder.preview.managePublishing', 'Gerenciar configurações de publicação')}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              <FormPreview formState={formState} mode={previewMode} />
            </div>
          </div>
        </div>
      )}

      {showShortcutsModal && (
        <div className="fixed inset-0 z-[60] bg-slate-950/45 backdrop-blur-sm">
          <div className="mx-auto mt-10 max-h-[calc(100vh-5rem)] w-[min(620px,calc(100vw-2rem))] overflow-hidden rounded-[28px] bg-slate-100 shadow-2xl dark:bg-gray-900">
            <div className="flex items-center justify-between px-6 py-5">
              <h2 className="break-words text-[clamp(1.05rem,2.8vw,1.375rem)] font-normal text-slate-900 dark:text-white">
                {tt('formBuilder.shortcuts.title', 'Keyboard shortcuts')}
              </h2>
              <button
                onClick={() => setShowShortcutsModal(false)}
                className="rounded-full p-2 text-slate-500 transition hover:bg-white/70 hover:text-slate-900 dark:hover:bg-gray-800 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 pb-4">
              <div className="flex items-center gap-3 rounded-full bg-white px-5 py-4 shadow-sm dark:bg-gray-800">
                <Search className="h-5 w-5 text-slate-500" />
                <input
                  type="text"
                  value={shortcutsQuery}
                  onChange={(e) => setShortcutsQuery(e.target.value)}
                  placeholder={tt('formBuilder.shortcuts.searchPlaceholder', 'Search')}
                  className="w-full border-none bg-transparent !p-0 !text-base text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
                />
              </div>
            </div>

            <div className="max-h-[calc(100vh-13rem)] overflow-y-auto px-6 pb-6">
              {filteredShortcutsSections.map((section) => (
                <section key={section.title} className="mb-8">
                  <h3 className="mb-4 text-[15px] font-semibold text-slate-700 dark:text-gray-200">
                    {section.title}
                  </h3>
                  <div className="rounded-2xl bg-transparent">
                    {section.items.map((item, index) => (
                      <div key={`${section.title}-${item.label}`} className={`${index > 0 ? 'border-t border-slate-300/80 dark:border-gray-700' : ''} py-4`}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="text-[15px] text-slate-900 dark:text-white">{item.label}</div>
                          <div className="flex flex-wrap items-center justify-end gap-1.5">
                            {item.keys.map((key) => (
                              <kbd key={`${item.label}-${key}`} className="rounded-md border border-slate-500 bg-white px-2 py-1 text-sm text-slate-800 shadow-sm dark:border-slate-500 dark:bg-gray-800 dark:text-gray-100">
                                {key}
                              </kbd>
                            ))}
                          </div>
                        </div>
                        {item.alt && (
                          <div className="mt-2 flex items-center justify-between gap-4">
                            <div className="text-sm text-slate-500 dark:text-gray-400">{tt('formBuilder.shortcuts.alt', 'or')}</div>
                            <div className="flex flex-wrap items-center justify-end gap-1.5">
                              {item.alt.map((key) => (
                                <kbd key={`${item.label}-alt-${key}`} className="rounded-md border border-slate-400 bg-white px-2 py-1 text-sm text-slate-700 shadow-sm dark:border-slate-500 dark:bg-gray-800 dark:text-gray-200">
                                  {key}
                                </kbd>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              ))}

              <button
                onClick={() => window.open('https://support.google.com/docs/', '_blank', 'noopener,noreferrer')}
                className="inline-flex items-center gap-2 text-lg font-medium text-brand-600 hover:text-brand-700"
              >
                <Link2 className="h-4 w-4" />
                {tt('formBuilder.shortcuts.helpCenter', 'View everything in the Help Center')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="fixed inset-0 z-[62] bg-slate-950/35 backdrop-blur-sm">
          <div className="mx-auto mt-10 w-[min(560px,calc(100vw-2rem))] overflow-hidden rounded-[28px] bg-white shadow-2xl dark:bg-gray-900">
            <div className="flex items-center justify-between px-6 py-5">
              <h2 className="text-[18px] font-medium text-slate-900 dark:text-white">
                {tt('formBuilder.share.title', 'Compartilhar "{{name}}"', {
                  name: formState.formTitle || tt('formBuilder.defaults.formTitle', 'Formulário sem título'),
                })}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShareModalView('help')}
                  className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-gray-800 dark:hover:text-white"
                >
                  <HelpCircle className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShareModalView('settings')}
                  className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-gray-800 dark:hover:text-white"
                >
                  <Settings className="h-5 w-5" />
                </button>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setShareModalView('main');
                  }}
                  className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-gray-800 dark:hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="px-6 pb-6">
              {shareModalView === 'help' && (
                <div className="space-y-6">
                  <button
                    onClick={() => setShareModalView('main')}
                    className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {tt('actions.back', 'Voltar')}
                  </button>

                  <div>
                    <h3 className="break-words text-[clamp(1.25rem,3.6vw,1.75rem)] font-normal text-slate-900 dark:text-white">
                      {tt('formBuilder.share.helpTitle', 'Ajuda e feedback')}
                    </h3>
                    <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">
                      {tt('formBuilder.share.helpDescription', 'Abra a central de ajuda ou envie feedback direto para a AIDA Forms.')}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-5 dark:border-gray-800">
                    <button
                      onClick={() => window.open('https://support.google.com/docs/', '_blank', 'noopener,noreferrer')}
                      className="mb-4 text-left text-base font-medium text-brand-600 hover:text-brand-700"
                    >
                      {tt('formBuilder.share.helpCenter', 'Ver tudo na Central de Ajuda')}
                    </button>
                    <div>
                      <button
                        onClick={() => toast.success(tt('formBuilder.share.feedbackSent', 'Canal de feedback da AIDA Forms acionado'))}
                        className="rounded-full bg-brand-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-brand-700"
                      >
                        {tt('formBuilder.share.sendFeedback', 'Enviar feedback para a AIDA Forms')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {shareModalView === 'settings' && (
                <div className="space-y-6">
                  <button
                    onClick={() => setShareModalView('main')}
                    className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {tt('actions.back', 'Voltar')}
                  </button>

                  <div>
                    <h3 className="break-words text-[clamp(1.25rem,3.6vw,1.75rem)] font-normal text-slate-900 dark:text-white">
                      {tt('formBuilder.share.settingsTitle', 'Configurações de "{{name}}"', {
                        name: formState.formTitle || tt('formBuilder.defaults.formTitle', 'Formulário sem título'),
                      })}
                    </h3>
                  </div>

                  <div>
                    <div className="mb-4 text-sm font-medium text-slate-900 dark:text-white">
                      {tt('formBuilder.share.access', 'Acessar')}
                    </div>
                    <label className="flex items-start gap-4 rounded-2xl border border-slate-200 p-4 dark:border-gray-800">
                      <input type="checkbox" checked readOnly className="mt-1 h-5 w-5 rounded" />
                      <div>
                        <div className="text-[15px] text-slate-900 dark:text-white">
                          {tt('formBuilder.share.editorsCanShare', 'Permitir que os editores mudem as permissões e compartilhem')}
                        </div>
                        <button
                          onClick={() => setShareModalView('help')}
                          className="mt-2 text-sm text-brand-600 hover:text-brand-700"
                        >
                          {tt('formBuilder.share.learnMore', 'Saiba mais sobre as configurações de compartilhamento dos editores')}
                        </button>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {shareModalView === 'main' && (
                <>
              <input
                type="text"
                placeholder={tt('formBuilder.share.addPeople', 'Adicionar participantes, grupos e eventos da agenda')}
                className="mb-6 w-full rounded-xl border border-brand-500 px-4 py-4 text-sm text-slate-900 outline-none placeholder:text-brand-600 dark:bg-gray-900 dark:text-white"
              />

              <div className="mb-8">
                <h3 className="mb-4 text-[16px] font-medium text-slate-900 dark:text-white">
                  {tt('formBuilder.share.peopleWithAccess', 'Pessoas com acesso')}
                </h3>
                <div className="flex items-center justify-between rounded-2xl px-2 py-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-600 text-sm font-semibold text-white">
                      {currentUserInitial}
                    </div>
                    <div>
                      <div className="text-[16px] font-medium text-slate-900 dark:text-white">
                        {currentUserName} ({tt('formBuilder.share.you', 'you')})
                      </div>
                      <div className="text-sm text-slate-500 dark:text-gray-400">
                        {currentUserEmail}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-slate-400 dark:text-gray-500">
                    {tt('formBuilder.share.owner', 'Proprietário')}
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="mb-4 text-[16px] font-medium text-slate-900 dark:text-white">
                  {tt('formBuilder.share.generalAccess', 'Acesso geral')}
                </h3>

                <div className="mb-5 flex items-start justify-between gap-4">
                  <div className="flex gap-3">
                    <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-gray-300">
                      <Lock className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-[15px] font-medium text-slate-900 dark:text-white">
                        {tt('formBuilder.share.editorView', 'Visualização de editor')}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                        {tt('formBuilder.share.editorViewDescription', 'Só as pessoas com acesso podem abrir usando o link.')}
                      </div>
                    </div>
                  </div>
                  <div className="min-w-[190px]">
                    <select
                      value={settingsUi.editorAccess}
                      onChange={(e) => updateSettingsUi({ editorAccess: e.target.value })}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                    >
                      <option value="restricted">{tt('formBuilder.share.restricted', 'Restrito')}</option>
                      <option value="internal">{tt('formBuilder.share.internal', 'Interno')}</option>
                    </select>
                  </div>
                </div>

                <div className="mb-5 flex items-start justify-between gap-4">
                  <div className="flex gap-3">
                    <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-full bg-brand-100 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                      <Globe className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-[15px] font-medium text-slate-900 dark:text-white">
                        {tt('formBuilder.share.participantView', 'Visualização de participante')}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                        {tt('formBuilder.share.participantViewDescription', 'Qualquer pessoa na Internet com o link pode responder')}
                      </div>
                    </div>
                  </div>
                  <div className="min-w-[190px]">
                    <select
                      value={settingsUi.participantAccess}
                      onChange={(e) => updateSettingsUi({ participantAccess: e.target.value })}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                    >
                      <option value="public">{tt('formBuilder.share.anyoneWithLink', 'Qualquer pessoa com o link')}</option>
                      <option value="restricted">{tt('formBuilder.share.restricted', 'Restrito')}</option>
                    </select>
                  </div>
                </div>

                <div className="rounded-2xl bg-brand-100/80 px-5 py-4 text-[15px] text-brand-900 dark:bg-brand-500/10 dark:text-brand-200">
                  {tt('formBuilder.share.publishHint', 'Publique o formulário para aceitar respostas')}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setShareModalView('main');
                  }}
                  className="rounded-full bg-brand-600 px-8 py-3 text-sm font-medium text-white transition hover:bg-brand-700"
                >
                  {tt('formBuilder.share.done', 'Concluído')}
                </button>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showAppsScriptModal && (
        <div className="fixed inset-0 z-[64] bg-slate-950/45 backdrop-blur-sm">
          <div className="mx-auto mt-8 max-h-[calc(100vh-4rem)] w-[min(980px,calc(100vw-2rem))] overflow-hidden rounded-[28px] bg-white shadow-2xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 dark:border-slate-800">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {tt('formBuilder.menu.appsScript', 'Apps Script')}
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {tt(
                    'formBuilder.menu.appsScriptDescription',
                    'Script pronto para colar no Google Apps Script e gerar um Google Form equivalente ao formulário atual.'
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyAppsScript}
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-700"
                >
                  <Copy className="h-4 w-4" />
                  {tt('formBuilder.menu.copyAppsScript', 'Copiar script')}
                </button>
                <button
                  onClick={() => setShowAppsScriptModal(false)}
                  className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="grid gap-0 md:grid-cols-[280px_minmax(0,1fr)]">
              <aside className="border-b border-slate-200 bg-slate-50 px-6 py-5 dark:border-slate-800 dark:bg-slate-950 md:border-b-0 md:border-r">
                <h3 className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                  {tt('formBuilder.menu.appsScriptSteps', 'Como usar')}
                </h3>
                <ol className="mt-4 space-y-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  <li>{tt('formBuilder.menu.appsScriptStep1', '1. Open a new project in Google Apps Script.')}</li>
                  <li>{tt('formBuilder.menu.appsScriptStep2', '2. Delete the default `myFunction()` function.')}</li>
                  <li>{tt('formBuilder.menu.appsScriptStep3', '3. Paste the script generated by AIDA Forms.')}</li>
                  <li>{tt('formBuilder.menu.appsScriptStep4', '4. Run `createAidaFormsForm` and authorize access.')}</li>
                  <li>{tt('formBuilder.menu.appsScriptStep5', '5. Check the links in the `Executions log` to open the form and spreadsheet.')}</li>
                </ol>

                <div className="mt-6 rounded-2xl border border-brand-200 bg-brand-50 px-4 py-4 text-sm text-brand-900 dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-100">
                  <div className="font-medium">{tt('formBuilder.menu.appsScriptLimitations', 'Limitações conhecidas')}</div>
                  <ul className="mt-2 space-y-2 text-[13px] leading-5">
                    <li>{tt('formBuilder.menu.appsScriptLimitationsTheme', 'Tema, CSS customizado e layout visual da AIDA Forms não são migrados para o Google Forms.')}</li>
                    <li>{tt('formBuilder.menu.appsScriptLimitationsLogic', 'Regras condicionais e eventos customizados entram só como comentário para ajuste manual.')}</li>
                    <li>{tt('formBuilder.menu.appsScriptLimitationsUpload', 'Upload de arquivo no Google Forms depende de Google Workspace e permissões do domínio.')}</li>
                  </ul>
                </div>
              </aside>

              <div className="min-w-0 px-6 py-5">
                <div className="mb-3 flex items-center justify-between gap-4">
                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                    {tt('formBuilder.menu.appsScriptGenerated', 'Script gerado')}
                  </div>
                  <div className="text-xs text-slate-400 dark:text-slate-500">
                    {formState.fields.length} {tt('formBuilder.menu.appsScriptFields', 'campos convertidos')}
                  </div>
                </div>

                <pre className="max-h-[calc(100vh-12rem)] overflow-auto rounded-2xl border border-slate-200 bg-slate-950 p-5 text-xs leading-6 text-slate-100 dark:border-slate-700">
                  <code>{buildAppsScript()}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {showThemePanel && (
        <div className="fixed inset-0 z-[65] bg-slate-950/10">
          <div className="absolute right-0 top-0 h-full w-[min(420px,100vw)] border-l border-slate-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 dark:border-gray-800">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {tt('formBuilder.themePanel.title', 'Personalizar tema')}
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                  {tt('formBuilder.themePanel.description', 'Ajuste as cores e a tipografia do formulário.')}
                </p>
              </div>
              <button
                onClick={() => setShowThemePanel(false)}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-gray-900 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="h-[calc(100%-89px)] overflow-y-auto px-4 py-5">
              <section className="mb-8">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-gray-500">
                  {tt('formBuilder.themePanel.presets', 'Estilos rápidos')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {themePresets.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => handleFormSettingsUpdate({
                        theme: {
                          ...formState.theme,
                          primaryColor: preset.primary,
                          secondaryColor: preset.secondary,
                          fontFamily: preset.fontFamily,
                        },
                      })}
                      className="rounded-2xl border border-slate-200 p-4 text-left transition hover:border-brand-300 hover:bg-brand-50/40 dark:border-gray-800 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/5"
                    >
                      <div className="mb-3 flex items-center gap-2">
                        <span className="h-5 w-5 rounded-full" style={{ backgroundColor: preset.primary }} />
                        <span className="h-5 w-5 rounded-full" style={{ backgroundColor: preset.secondary }} />
                      </div>
                      <div className="text-sm font-medium text-slate-900 dark:text-white">{preset.name}</div>
                    </button>
                  ))}
                </div>
              </section>

              <section className="mb-8">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-gray-500">
                  {tt('formBuilder.themePanel.colors', 'Cores')}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-900 dark:text-white">
                      {tt('formBuilder.themePanel.primaryColor', 'Cor principal')}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={formState.theme.primaryColor}
                        onChange={(e) => handleFormSettingsUpdate({
                          theme: { ...formState.theme, primaryColor: e.target.value },
                        })}
                        className="h-12 w-14 cursor-pointer rounded-xl border border-slate-300 bg-white dark:border-gray-700 dark:bg-gray-900"
                      />
                      <input
                        type="text"
                        value={formState.theme.primaryColor}
                        onChange={(e) => handleFormSettingsUpdate({
                          theme: { ...formState.theme, primaryColor: e.target.value },
                        })}
                        className="flex-1 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-900 dark:text-white">
                      {tt('formBuilder.themePanel.secondaryColor', 'Cor de destaque')}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={formState.theme.secondaryColor}
                        onChange={(e) => handleFormSettingsUpdate({
                          theme: { ...formState.theme, secondaryColor: e.target.value },
                        })}
                        className="h-12 w-14 cursor-pointer rounded-xl border border-slate-300 bg-white dark:border-gray-700 dark:bg-gray-900"
                      />
                      <input
                        type="text"
                        value={formState.theme.secondaryColor}
                        onChange={(e) => handleFormSettingsUpdate({
                          theme: { ...formState.theme, secondaryColor: e.target.value },
                        })}
                        className="flex-1 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-gray-500">
                  {tt('formBuilder.themePanel.typography', 'Tipografia')}
                </h3>
                <label className="mb-2 block text-sm font-medium text-slate-900 dark:text-white">
                  {tt('formBuilder.themePanel.fontFamily', 'Fonte do formulário')}
                </label>
                <select
                  value={formState.theme.fontFamily}
                  onChange={(e) => handleFormSettingsUpdate({
                    theme: { ...formState.theme, fontFamily: e.target.value },
                  })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                >
                  {fontOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-400 dark:text-gray-500">
                  {tt('formBuilder.themePanel.preview', 'Prévia')}
                </h3>
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 dark:border-gray-800 dark:bg-gray-900">
                  <div
                    className="h-3"
                    style={{
                      background: `linear-gradient(90deg, ${formState.theme.primaryColor}, ${formState.theme.secondaryColor})`,
                    }}
                  />
                  <div className="p-5" style={{ fontFamily: formState.theme.fontFamily }}>
                    <div className="text-xl text-slate-900 dark:text-white">
                      {formState.formTitle || tt('formBuilder.defaults.formTitle', 'Formulário sem título')}
                    </div>
                    <div className="mt-2 text-sm text-slate-500 dark:text-gray-400">
                      {formState.formDescription || tt('formBuilder.preview.descriptionFallback', 'Adicione uma descrição curta para explicar o propósito deste formulário.')}
                    </div>
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-400 dark:border-gray-700 dark:bg-gray-900">
                      {tt('formBuilder.themePanel.inputPreview', 'Prévia do campo')}
                    </div>
                    <button
                      type="button"
                      style={{ backgroundColor: formState.theme.primaryColor }}
                      className="mt-4 rounded-2xl px-4 py-2.5 text-sm font-medium text-white"
                    >
                      {formState.submitButtonText || tt('formBuilder.defaults.submitButton', 'Enviar')}
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormBuilder;
